import * as DSAssetFamilies from 'customer-data-filters-ui/filterQueryFormat/DSAssetFamilies/DSAssetFamilies';
import { makeFuzzyRegExp } from 'customer-data-objects/search/FieldSearch';
import { Map as ImmutableMap } from 'immutable';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { List } from 'immutable';
import { useStrategy } from './useStrategy';
import { useTranslationHelpers } from './useTranslationHelpers';
import has from 'transmute/has';
import I18n from 'I18n';
import SegmentationFiltersInstanceContext from '../state/SegmentationFiltersInstanceContext';
import { isEventBasedFilterFamily } from '../strategy/eventBasedFilters';
import { useDisableFields } from './useDisableFields';
import { useEditorActions } from './useEditorActions';
import MissingField from 'customer-data-filters-ui/filterQueryFormat/MissingField';
import { getDynamicFilterFamilyBasis } from '../filterQueryFormat/DynamicFilterFamilies';
import { isRelativeTimeProperty } from '../utils/isRelativeTimeProperty';
import { hasRelativeDateFilteringCrmOnly } from '../permissions/gates';
import { resolveIsUngated } from '../utils/UserInfoHelper';
import { CrmSearch, SalesViews } from '../filterQueryFormat/strategy/StrategyTypes';
import { stripDiacritics } from 'foundations-components/transitional/utils/Diacritics';
import { getOverriddenFieldType } from '../utils/getIconComponent';
const filterFamiliesNotToSort = Object.freeze({
  [DSAssetFamilies.FORM]: true,
  [DSAssetFamilies.CTA]: true,
  [DSAssetFamilies.EMAIL_CAMPAIGN]: true
});
export const shouldFilterOptionsForFilterFamily = filterFamily => {
  // These filter families use crm search resolvers to fetch options,
  // since the backend filters them less strictly we should not filter them again.
  // Filtering on the FE can cause for nothing to be shown when there are more results.
  const filterFamilyBasis = getDynamicFilterFamilyBasis(filterFamily) || filterFamily;
  if ([DSAssetFamilies.CTA, DSAssetFamilies.EMAIL_CAMPAIGN, DSAssetFamilies.FORM, DSAssetFamilies.IN_LIST].includes(filterFamilyBasis)) {
    return false;
  }
  return true;
};

// sorting need to be disabled for some filter categories
// see https://git.hubteam.com/HubSpot/crm-filters/issues/907
const shouldBeSorted = filterFamily => {
  // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
  return !filterFamiliesNotToSort[filterFamily];
};

// Sort function for strings tailored for search
// prioritize case-insensitive starts-with matches over the rest (eg: contains matches)
export const getSortFnForSearch = target => {
  return (a, b) => {
    const targetLower = target === null || target === void 0 ? void 0 : target.toLocaleLowerCase();
    const aStartsWithTarget = targetLower ? a.toLocaleLowerCase().startsWith(targetLower) : false;
    const bStartsWithTarget = targetLower ? b.toLocaleLowerCase().startsWith(targetLower) : false;

    // If both or neither start with target, use localeCompare
    if (aStartsWithTarget === bStartsWithTarget) {
      return a.localeCompare(b);
    }
    // If only one starts with target, sort that one first
    return aStartsWithTarget ? -1 : 1;
  };
};
export const utils = {
  _isHeadingEntry: entry => has('heading', entry),
  _isSubheadingEntry: entry => has('properties', entry),
  _isSelectableEntry: entry => entry && !utils._isHeadingEntry(entry) && !utils._isSubheadingEntry(entry),
  getSortedFields: (getLabelString, fields, filterFamily, searchString, hiddenProperties = [], fieldTypeFilter) => {
    const filteredFields = fields.valueSeq().filter(field => !hiddenProperties.includes(field.name)).filter(field => {
      const fieldType = getOverriddenFieldType(field, resolveIsUngated(), filterFamily);
      return fieldTypeFilter !== null && fieldTypeFilter !== void 0 && fieldTypeFilter.length ? fieldTypeFilter.includes(fieldType) : true;
    });
    if (shouldBeSorted(filterFamily)) {
      return filteredFields.sortBy(getLabelString, getSortFnForSearch(searchString)).toList();
    }
    return filteredFields.toList();
  },
  getSortedFieldGroups: (getLabelString, fieldGroups, filterFamily, searchString, hiddenProperties = [], fieldTypeFilter) => {
    return fieldGroups.valueSeq().sortBy(({
      displayOrder
    }) => {
      return displayOrder;
    }).map(group => group.update('properties', properties => {
      const formattedProperties = properties.filter(property => !hiddenProperties.includes(property.name)).filter(property => {
        const fieldType = getOverriddenFieldType(property, resolveIsUngated(), filterFamily);
        return fieldTypeFilter !== null && fieldTypeFilter !== void 0 && fieldTypeFilter.length ? fieldTypeFilter.includes(fieldType) : true;
      }).map(property => property.set('displayName', getLabelString(property)));
      if (shouldBeSorted(filterFamily)) {
        const sorted = formattedProperties.sortBy(property => property.displayName, getSortFnForSearch(searchString));
        return sorted;
      }
      return formattedProperties;
    })).toList();
  },
  getFilteredFields: (getLabelString, fields, search, filterFamily, strategyType) => {
    const searchQueryWithNoDiacritics = typeof search === 'string' ? stripDiacritics(search) : search;
    const pattern = makeFuzzyRegExp(searchQueryWithNoDiacritics);
    const result = [];
    const fieldsLength = fields.size;
    for (let index = 0; index < fieldsLength; index++) {
      const field = fields.get(index);
      if (field.constructor === MissingField) {
        continue;
      }
      if (isRelativeTimeProperty(field)) {
        if (strategyType === CrmSearch || strategyType === SalesViews) {
          if (!hasRelativeDateFilteringCrmOnly(resolveIsUngated())) {
            continue;
          }
        }
      }
      const labelString = getLabelString(field);
      const labelStringWithNoDiacritics = typeof labelString === 'string' ? stripDiacritics(labelString) : labelString;
      if (!shouldFilterOptionsForFilterFamily(filterFamily) || pattern.test(labelStringWithNoDiacritics)) {
        result.push(field.set('displayName', labelString));
      }
    }
    return result;
  },
  getGroupedFilteredFields: (getLabelString, fieldGroups, search, filterFamily, strategyType) => {
    const result = [];
    const groupsLength = fieldGroups.size;
    let lastGroupWasMostUsedProps = false;
    for (let i = 0; i < groupsLength; i++) {
      const group = fieldGroups.get(i);
      let groupLength;
      if (group.properties.size === 0) {
        continue;
      }
      if (lastGroupWasMostUsedProps) {
        result.push(ImmutableMap({
          heading: 'allProperties',
          displayName: I18n.text('filterSidebar.allProperties')
        }));
        lastGroupWasMostUsedProps = false;
      }
      if (group.name === 'most_used_properties') {
        groupLength = result.push(ImmutableMap({
          heading: 'mostUsedProperties',
          displayName: I18n.text('filterSidebar.mostUsedProperties')
        }));
        lastGroupWasMostUsedProps = true;
      } else {
        groupLength = result.push(group.set('displayName', group.displayName));
      }
      const properties = group.properties;
      result.push(...utils.getFilteredFields(getLabelString, properties, search, filterFamily, strategyType));
      if (result.length === groupLength) {
        result.pop();
      }
    }
    const isResultEmpty = !result.some(utils._isSelectableEntry);
    return isResultEmpty ? [] : result;
  }
};
export function useFieldOptions(filterFamily, searchString, hiddenProperties, fieldTypeFilter) {
  const {
    getFields,
    getFieldGroups,
    getRefinementFieldGroups,
    shouldSelectTheOnlyFieldAutomatically,
    getCustomFilterFamilyFieldsGroup,
    strategyType
  } = useStrategy();
  const {
    getFieldLabelString
  } = useTranslationHelpers(filterFamily);
  const {
    conditionToRefineKeyPath
  } = useContext(SegmentationFiltersInstanceContext);
  const {
    disableFields,
    disableFieldGroups
  } = useDisableFields(filterFamily);
  const {
    selectField
  } = useEditorActions();
  const [fields, setFields] = useState();
  useEffect(() => {
    const loadFields = async () => {
      const loadedFields = await getFields(filterFamily);
      setFields(disableFields(loadedFields));
    };
    void loadFields();
  }, [filterFamily, getFields, disableFields]);
  const fieldGroups = useMemo(() => {
    let allFieldGroups;
    const customGroup = getCustomFilterFamilyFieldsGroup(filterFamily);
    if (conditionToRefineKeyPath && isEventBasedFilterFamily(filterFamily)) {
      allFieldGroups = getRefinementFieldGroups(filterFamily);
    }
    //
    else if (customGroup) {
      const fieldsList = fields ? fields.toList() : List();
      allFieldGroups = List([customGroup.set('properties', fieldsList)]);
    }
    //
    else {
      allFieldGroups = getFieldGroups(filterFamily);
    }
    return disableFieldGroups(allFieldGroups);
  }, [fields, filterFamily, getFieldGroups, disableFieldGroups, conditionToRefineKeyPath, getRefinementFieldGroups, getCustomFilterFamilyFieldsGroup]);
  const [sortedFields, setSortedFields] = useState(utils.getSortedFields(getFieldLabelString, fields || ImmutableMap(), filterFamily, searchString, hiddenProperties, fieldTypeFilter));
  const [sortedFieldGroups, setSortedFieldGroups] = useState(utils.getSortedFieldGroups(getFieldLabelString, fieldGroups || ImmutableMap(), filterFamily, searchString, hiddenProperties, fieldTypeFilter));
  useEffect(() => {
    setSortedFieldGroups(utils.getSortedFieldGroups(getFieldLabelString, fieldGroups || ImmutableMap(), filterFamily, searchString, hiddenProperties, fieldTypeFilter));
    setSortedFields(utils.getSortedFields(getFieldLabelString, fields || ImmutableMap(), filterFamily, searchString, hiddenProperties, fieldTypeFilter));
  }, [fields, fieldGroups, getFieldLabelString, filterFamily, searchString, hiddenProperties, fieldTypeFilter]);
  const options = sortedFieldGroups.size > 0 ? utils.getGroupedFilteredFields(getFieldLabelString, sortedFieldGroups, searchString, filterFamily, strategyType) : utils.getFilteredFields(getFieldLabelString, sortedFields, searchString, filterFamily, strategyType);
  const selectTheOnlyField = useCallback(() => {
    const selectTheOnlyFieldAutomatically = shouldSelectTheOnlyFieldAutomatically(filterFamily);
    if (selectTheOnlyFieldAutomatically && options.length === 1) {
      selectField(filterFamily, options[0]);
    }
  }, [filterFamily, options, selectField, shouldSelectTheOnlyFieldAutomatically]);
  return {
    options,
    selectTheOnlyField
  };
}